import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Form } from 'reactstrap';
import { useMyCompanyType } from '../../../../store/Auth/authHooks';
import { useCloseOldModal } from '../../../../store/modal/modalHooks';
import {
  useFetchResourceAlloc,
  useResourceManagementSlots,
  useSaveResourceAlloc,
} from '../../../../store/RoomManagement/resourceManagementHooks';
import { RoomResource } from '../../../../store/RoomManagement/resourceManagementTypes';
import { getTimeStamp } from '../../../../utility/functions/getTimeString';
import { stringHour } from '../../../../utility/functions/stringHour';
import { useQuery } from '../../../../utility/hooks/useQuery';
import isCreditDisabled from '../../../../utility/services/is-credit-disabled';
import BookingAddCheckout from '../../../shared/BookingAddCheckout';
import { CreditTypesEnum } from '../../../shared/BookingCheckout';
import BookRoomForm from '../BookRoomForm';
import './index.scss';

type PropsTypes = {
  form: UseFormReturn<any, object>;
  room: RoomResource;
  setShowSuccessPage: (value: boolean) => void;
  setShowCancellationPolicy: (value: boolean) => void;
  showCancellationPolicy: boolean;
  onClickInvitedUsers: () => void;
};

const BookRoomFormContainer = (props: PropsTypes) => {
  const { t } = useTranslation();
  const closeModal = useCloseOldModal();
  const { clearQuery } = useQuery();
  const companyType = useMyCompanyType();
  const saveResourceAlloc = useSaveResourceAlloc();
  const resourceSlots = useResourceManagementSlots();
  const fetchResourceAlloc = useFetchResourceAlloc();
  const [totalPrice, setTotalPrice] = useState<number>(0);

  useEffect(() => {
    let minimumPrice = props.room.bookingPrice;

    const specialPrice = props.room.specialPrices?.find(
      (special) => special.companyType === companyType?.id
    );

    if (specialPrice && specialPrice.price < minimumPrice) {
      minimumPrice = specialPrice.price;
    }

    const sortedPricingRules = [...props.room.pricingRules].sort(
      (a, b) => b.minHours - a.minHours
    );

    const pricingRule = sortedPricingRules.find(
      (rule) =>
        rule.minHours <= props.form.watch('end') - props.form.watch('start')
    );

    if (pricingRule && pricingRule.bookingPrice < minimumPrice) {
      minimumPrice = pricingRule.bookingPrice;
    }

    setTotalPrice(
      (props.form.watch('end') - props.form.watch('start')) * minimumPrice
    );
  }, [
    props.form.watch('end'),
    props.form.watch('start'),
    props.room.bookingPrice,
    companyType,
  ]);

  useEffect(() => {
    if (props.form.watch('date')?.length) {
      const timeStamp = new Date(props.form.watch('date')[0]).getTime();
      fetchResourceAlloc(props.room.id, timeStamp).finally();
    }
  }, [props.form.watch('date')?.toString()]);

  const onSuccess = () => {
    props.setShowSuccessPage(true);
  };

  const onFailure = (errorMessage: string) => {
    if (
      errorMessage === 'Allocation start time invalid' &&
      props.room.hoursUntilBookingAllowed
    ) {
      errorMessage = t('CANNOT_RESERVE_LESS_ERROR_MESSAGE', {
        hours: props.room.hoursUntilBookingAllowed,
      });
    }
    if (
      errorMessage === 'Allocation pre booking time invalid' &&
      props.room.preBookingTime &&
      props.room.preBookingDays
    ) {
      errorMessage = t('RESERVE_ROOM_PRE_ERROR_MESSAGE');
    }
    toast.error(errorMessage, {
      icon: false,
      hideProgressBar: true,
      autoClose: 5000,
    });
  };

  const onSubmit = async (data: any) => {
    const allocation = {
      start_time: getTimeStamp(data.date[0], stringHour(data.start)),
      end_time: getTimeStamp(data.date[0], stringHour(data.end)),
      resource_id: props.room.id,
      number_of_participants: data.numberOfParticipants,
      user_message: props.form.watch('messageForAdmin'),
      invited_users: data.invited_users,
      department_name: data.departmentName,
      meeting_subject: data.meetingSubject,
    };

    const { isSuccessfull, errorMessage } = await saveResourceAlloc(allocation);
    if (isSuccessfull) {
      onSuccess();
    } else {
      onFailure(errorMessage);
    }
  };

  const onShowPolicy = () => {
    if (props.showCancellationPolicy) {
      props.setShowCancellationPolicy(false);
    } else {
      props.setShowCancellationPolicy(true);
    }
  };

  const onCloseModal = () => {
    closeModal();
    clearQuery('resourceId');
  };

  return (
    <Form onSubmit={props.form.handleSubmit(onSubmit)}>
      <div className="book-room-modal-form">
        <BookRoomForm
          form={props.form}
          room={props.room}
          originalArray={resourceSlots}
          onClickInvitedUsers={props.onClickInvitedUsers}
        />
        <BookingAddCheckout
          creditTypes={CreditTypesEnum.COMPANY}
          price={totalPrice}
          disabledCreditUser={isCreditDisabled(
            props.room.enableCreditUse,
            props.form.watch('date')
          )}
          discount={
            props.room.isPremium
              ? companyType?.premiumDiscount
              : companyType?.discount
          }
        />
        <div className="book-room-form-actions">
          <Button
            disabled={
              !Boolean(props.form.watch('start') && props.form.watch('end'))
            }
            className="confirm-booking-button"
            type="submit"
            color="primary"
          >
            {t('CONFIRM_BOOKING')}
          </Button>
          <Button
            className="cancel-booking-button"
            type="button"
            color=""
            onClick={onCloseModal}
          >
            {t('CREATE_NEW_SERVICE_CALL_CLOSE_BUTTON')}
          </Button>{' '}
        </div>
        <div className="view-cancellation-policy-btn">
          <span onClick={() => onShowPolicy()}>
            {props.showCancellationPolicy
              ? t('BACK_BUTTON_TEXT')
              : t('VIEW_CANCELLATION_POLICY')}
          </span>
        </div>
      </div>
    </Form>
  );
};

export default BookRoomFormContainer;
